// frontend/src/routes/ProtectedRouteDevioz.tsx
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';

/** Extrae códigos de rol de múltiples formatos: "DEVIOZ_*", { code }, { name }, arrays anidados, nullables */
function roleCodesFrom(input: unknown): string[] {
  const out: string[] = [];

  const visit = (val: any) => {
    if (!val) return;
    if (Array.isArray(val)) {
      val.forEach(visit);
      return;
    }
    if (typeof val === 'string') {
      out.push(val);
      return;
    }
    if (typeof val === 'object') {
      const maybe = ((val as any).code ?? (val as any).name ?? '').toString();
      if (maybe) out.push(maybe);
    }
  };

  visit(input);
  return out.filter(Boolean);
}

/** Determina si existe un token válido en storage */
function hasToken(): boolean {
  const raw =
    localStorage.getItem('token') ??
    sessionStorage.getItem('token') ??
    localStorage.getItem('authToken') ??
    sessionStorage.getItem('authToken') ??
    '';
  if (!raw) return false;
  const token = raw.startsWith('Bearer ') ? raw.slice(7).trim() : raw.trim();
  return token.length > 0;
}

/** Intenta obtener el usuario desde el AuthContext o desde storage */
function resolveUser(ctxUser: unknown | undefined): any | undefined {
  if (ctxUser && typeof ctxUser === 'object') return ctxUser as any;

  const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user') ?? '';
  if (!raw) return undefined;

  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

type Props = {
  children: ReactNode;
  /** A dónde redirigir si NO tiene rol Devioz */
  fallbackPath?: string;
  /** A dónde redirigir si NO hay sesión */
  loginPath?: string;
  /** (Opcional) exigir que el usuario pertenezca a la compañía Devioz (company_id === DEVIOZ_COMPANY_ID) */
  requireDeviozCompany?: boolean;
};

export default function ProtectedRouteDevioz({
  children,
  fallbackPath = '/unauthorized',
  loginPath = '/',
  requireDeviozCompany = false,
}: Props) {
  const location = useLocation();
  const auth = useAuth() as { user?: any; loading?: boolean } | undefined;

  // Estado de carga del contexto de auth
  if (auth?.loading) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5">
        <div className="spinner-border text-dark me-2" role="status" />
        <span>Cargando…</span>
      </div>
    );
  }

  // Requiere token
  if (!hasToken()) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Resuelve usuario
  const user = resolveUser(auth?.user);
  if (!user) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Verifica rol DEVIOZ_*
  const codes = roleCodesFrom((user as any).roles);
  const hasDeviozRole = codes.some((c) => String(c).toUpperCase().startsWith('DEVIOZ_'));

  if (!hasDeviozRole) {
    return <Navigate to={fallbackPath} replace />;
  }

  // (Opcional) verificar que pertenece a la compañía Devioz si se requiere
  if (requireDeviozCompany) {
    const companyId = Number((user as any).company_id);
    const expected = Number(import.meta.env.VITE_DEVIOZ_COMPANY_ID ?? 1);
    if (!Number.isFinite(companyId) || companyId !== expected) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
}
